import { AddOutlined, FileDownloadOutlined } from '@mui/icons-material';
import { Box, Button, Paper } from '@mui/material';
import { Filter, FilterChips, PoolSpinner, PoolSpinnerProps } from '@synergy/components';
import { FormattedMessage } from 'react-intl';
import { Link, useSearchParams } from 'react-router-dom';
import {
    PaloBackButton,
    PaloBreadcrumb,
    PaloBreadcrumbs,
    PaloPageCrumb,
    usePaloHeadSlot,
} from '@synergy/components-palo';
import { PaloThemeProvider, tokens } from '@synergy/theme-palo';
import { PropsWithChildren, ReactElement, ReactNode, useEffect } from 'react';
import { createPortal } from 'react-dom';

export interface PaloOverviewProps extends PropsWithChildren, PoolSpinnerProps {
    titleMessage?: ReactNode;
    /** Base route of the screen; enables the create button together with newLabelMessage. */
    pathname?: string;
    newLabelMessage?: ReactNode;
    requesting?: boolean;
    filters?: ReactNode;
    useLegacyFilter?: boolean;
    /** Header slots, kept named as on the legacy overview so screens port over. */
    additionalItemsLeft?: ReactNode;
    additionalItemsRight?: ReactNode;
    additionalItemsBottom?: ReactNode;
    defaultSearchParams?: URLSearchParams;
    tabs?: ReactNode;
    /** Docked panel at the foot of the screen, e.g. a PaloActivityDock. */
    dock?: ReactNode;
    /** Back affordance; rendered left of the title, as on PaloDetailView. */
    onBack?: () => void;
    /**
     * Where the screen sits. A screen that is a record rather than a list —
     * the bonus settlement, an import run — names its place the way the
     * document masks do, and its head row then moves up into the top bar.
     */
    breadcrumbs?: PaloBreadcrumb[];
    /**
     * Content that brings its own frame — the bonus settlement details, whose
     * table measures itself against its scroller. The canvas card would put a
     * second one around it, and a virtualised table then renders nothing.
     */
    plain?: boolean;
    onResetData?: () => void;
    onExport?: () => void;
}

/**
 * PALO chrome for the list screens that are not built on PaloListView: canvas,
 * title, header actions, filter row and tabs. Mirrors the legacy Overview's
 * props so a screen only swaps the component, but the legacy one stays for the
 * customer, supplier and shop apps.
 */
export const PaloOverview = ({
    requesting,
    titleMessage,
    pathname,
    newLabelMessage,
    filters,
    useLegacyFilter,
    additionalItemsLeft,
    additionalItemsRight,
    additionalItemsBottom,
    defaultSearchParams,
    tabs,
    dock,
    plain,
    onBack,
    breadcrumbs,
    onResetData,
    onExport,
    enabled: _spinnerEnabled,
    children,
    ...spinnerProps
}: PaloOverviewProps): ReactElement => {
    const [searchParams, setSearchParams] = useSearchParams();
    const hasCrumbs = Boolean(breadcrumbs && breadcrumbs.length > 0);
    // Either way the screen names itself in the bar: a record with its whole
    // head row, a list with its title alone.
    const headSlot = usePaloHeadSlot(hasCrumbs || Boolean(titleMessage));
    const inHeadSlot = hasCrumbs && null !== headSlot.element;

    useEffect(() => {
        onResetData?.();
        return () => {
            onResetData?.();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const headRow = (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: inHeadSlot ? '10px' : '14px',
                // In the top bar the row fills the width and keeps to one
                // line; written inline it keeps the margin below it.
                ...(inHeadSlot ? { width: '100%', minWidth: 0, flexWrap: 'nowrap' } : { mb: '11px', flexWrap: 'wrap' }),
            }}
        >
            {hasCrumbs ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px', flexShrink: 0 }}>
                    <PaloBreadcrumbs items={breadcrumbs ?? []} />
                    <Box component="span" sx={{ color: tokens.color.ink3, opacity: 0.55, fontSize: '14px' }}>
                        /
                    </Box>
                </Box>
            ) : (
                onBack && <PaloBackButton onClick={onBack} />
            )}
            <Box
                sx={{
                    fontSize: hasCrumbs ? '14px' : '17px',
                    fontWeight: 640,
                    letterSpacing: '-.015em',
                    color: tokens.color.ink,
                    // In the bar the title is the one part that may give way:
                    // it is the longest and the only one that can be read
                    // from the record below it.
                    ...(inHeadSlot && {
                        minWidth: 0,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }),
                }}
            >
                {titleMessage}
            </Box>
            {inHeadSlot ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px', flexShrink: 0 }}>
                    {additionalItemsLeft}
                </Box>
            ) : (
                additionalItemsLeft
            )}
            <Box
                sx={{
                    marginLeft: 'auto',
                    display: 'flex',
                    gap: '7px',
                    alignItems: 'center',
                    ...(inHeadSlot ? { flexWrap: 'nowrap', flexShrink: 0 } : { flexWrap: 'wrap' }),
                }}
            >
                {filters}
                {onExport && (
                    <Button size="small" startIcon={<FileDownloadOutlined />} onClick={() => onExport()}>
                        <FormattedMessage id="app.common.export" />
                    </Button>
                )}
                {additionalItemsRight}
                {newLabelMessage && pathname && (
                    <Button
                        component={Link}
                        to={pathname + '/new'}
                        size="small"
                        variant="contained"
                        color="primary"
                        startIcon={<AddOutlined />}
                    >
                        {newLabelMessage}
                    </Button>
                )}
            </Box>
        </Box>
    );

    return (
        <PoolSpinner enabled={requesting} sx={{ display: 'block', height: '100%' }} {...spinnerProps}>
            {/* A plain list keeps its heading where it is and only lends the
                bar its name; a record hands over the whole row. */}
            {!hasCrumbs && titleMessage && <PaloPageCrumb>{titleMessage}</PaloPageCrumb>}
            <Filter
                searchParams={searchParams}
                defaultSearchParams={defaultSearchParams}
                onUpdateSearchParams={setSearchParams}
                useLegacyFilter={useLegacyFilter}
            >
                <PaloThemeProvider>
                    <Box
                        sx={{
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            backgroundColor: tokens.color.canvas,
                        }}
                    >
                        <Box
                            sx={{
                                backgroundColor: tokens.color.surface,
                                borderBottom: `1px solid ${tokens.color.line}`,
                                padding: inHeadSlot ? '0 16px' : '11px 16px 0',
                                flexShrink: 0,
                            }}
                        >
                            {/* A portal keeps the React context of where it is
                                written, so the filters in this row still reach
                                the Filter provider above. */}
                            {inHeadSlot ? createPortal(headRow, headSlot.element as HTMLElement) : headRow}
                            {tabs}
                            {additionalItemsBottom}
                        </Box>

                        <FilterChips />
                        {/* The list sits on the canvas as a card, the way every
                            PaloListView does — without this the screens on this
                            shell ran edge to edge in white while the ones next
                            to them in the same menu did not. */}
                        {/* Neither layer may scroll: the lists on this shell bring
                            their own scroller, and a second one around it leaves a
                            virtualised table measuring the wrong height — its rows
                            then render outside the visible area. */}
                        {plain ? (
                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
                                {children}
                            </Box>
                        ) : (
                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', p: 4 }}>
                                <Paper
                                    sx={{
                                        boxShadow: tokens.shadow.s1,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        flex: 1,
                                        minHeight: 0,
                                        overflow: 'hidden',
                                    }}
                                >
                                    {children}
                                </Paper>
                            </Box>
                        )}
                        {dock}
                    </Box>
                </PaloThemeProvider>
            </Filter>
        </PoolSpinner>
    );
};
